import React, { useEffect, useState } from 'react';
import { Container, Form, Button, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FiMoon, FiSun, FiMonitor, FiMenu } from 'react-icons/fi';
import InfoTooltip from '@core/components/InfoTooltip';
import {
  getActivityEntryAutoSaveEnabled,
  setActivityEntryAutoSaveEnabled,
} from '@features/report/activity-entry/utils/activityEntryPreferences';

type ThemePreference = 'light' | 'dark' | 'system';

function resolveTheme(theme: ThemePreference): 'light' | 'dark' {
  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  return theme;
}

const SettingsPage: React.FC = () => {
  const [theme, setTheme] = useState<ThemePreference>(() => {
    return (localStorage.getItem('ntcog-theme') as ThemePreference) || 'light';
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    return localStorage.getItem('ntcog-sidebar-collapsed') === 'true';
  });
  const [activityEntryAutoSaveEnabled, setActivityEntryAutoSaveEnabledState] =
    useState<boolean>(() => getActivityEntryAutoSaveEnabled());

  useEffect(() => {
    const applyTheme = () => {
      document.documentElement.setAttribute('data-bs-theme', resolveTheme(theme));
    };

    applyTheme();

    if (theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => applyTheme();
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [theme]);

  const handleThemeChange = (newTheme: ThemePreference) => {
    setTheme(newTheme);
    localStorage.setItem('ntcog-theme', newTheme);
  };

  const handleSidebarPreferenceChange = (nextValue: boolean) => {
    setSidebarCollapsed(nextValue);
    localStorage.setItem('ntcog-sidebar-collapsed', String(nextValue));
    window.dispatchEvent(new Event('ntcog-sidebar-preference-updated'));
  };

  const handleActivityEntryAutoSaveChange = (nextValue: boolean) => {
    setActivityEntryAutoSaveEnabledState(nextValue);
    setActivityEntryAutoSaveEnabled(nextValue);
  };

  return (
    <Container fluid className="py-4">
      <div className="ntcog-page-title-block">
        <h1 className="ntcog-page-heading mb-1">System Settings</h1>
        <p className="text-muted mb-0">Manage app-wide preferences and workspace behavior.</p>
      </div>

      {/* <Alert variant="info" className="mt-4">
        Personal details are now managed on the{' '}
        <Link to="/user/profile" className="fw-semibold">
          Profile
        </Link>{' '}
        page. This page now only shows preferences that are actually supported in the current system.
      </Alert> */}

      <div className="ntcog-form-section mb-4">
        <div className="d-flex align-items-center gap-2 mb-3">
          <FiMoon className="text-primary" />
          <h6 className="mb-0">Appearance</h6>
        </div>
        <p className="text-muted small mb-3">
          Choose how the application should look across the system.
        </p>
        <div
          className="d-flex gap-3 flex-wrap ntcog-theme-toggle"
          role="group"
          aria-label="Theme preference"
        >
          {([
            { value: 'light', label: 'Light', icon: <FiSun size={18} className="flex-shrink-0" /> },
            { value: 'dark', label: 'Dark', icon: <FiMoon size={18} className="flex-shrink-0" /> },
            { value: 'system', label: 'System', icon: <FiMonitor size={18} className="flex-shrink-0" /> },
          ] as const).map((option) => (
            <Button
              key={option.value}
              variant={theme === option.value ? 'primary' : 'outline-secondary'}
              className="d-inline-flex align-items-center justify-content-center gap-2 px-4 py-2"
              onClick={() => handleThemeChange(option.value)}
            >
              {option.icon}
              {option.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="ntcog-form-section">
        <div className="d-flex align-items-center gap-2 mb-3">
          <FiMenu className="text-primary" />
          <h6 className="mb-0">Workspace</h6>
        </div>
        <p className="text-muted small mb-3">
          Configure how the interface behaves when you move around the system.
        </p>

        <Form.Check
          type="switch"
          id="settings-sidebar-collapsed"
          label={
            <span>
              Start with the sidebar collapsed
              <InfoTooltip text="This preference controls the default sidebar width across entity dashboards." />
            </span>
          }
          checked={sidebarCollapsed}
          onChange={(e) => handleSidebarPreferenceChange(e.target.checked)}
        />
        <div className="text-muted small mt-1">
          Enable this if you want more screen space by default when opening dashboard pages.
        </div>

        <hr className="my-4" />

        <Form.Check
          type="switch"
          id="settings-activity-entry-autosave"
          label={
            <span>
              Enable activity submission draft auto-save
              <InfoTooltip text="When enabled, the activity submission form saves draft changes automatically after a short period of inactivity. This preference is stored in your browser." />
            </span>
          }
          checked={activityEntryAutoSaveEnabled}
          onChange={(e) => handleActivityEntryAutoSaveChange(e.target.checked)}
        />
        <div className="text-muted small mt-1">
          Keep this on to auto-save activity entry drafts while you work. You can still use the floating save button or
          {' '}<span className="fw-semibold">Ctrl+S</span>{' '}on the submission page at any time.
        </div>
      </div>
    </Container>
  );
};

export default SettingsPage;
